import { Check, Headset, ImageIcon, Paperclip, Video, X } from 'lucide-react'

import {
  SupportRequestModalContext,
  SupportRequestReason,
} from '@/app/entities/SupportRequest'
import { cn } from '@/app/utils/cn'
import { cpfMask } from '@/app/utils/cpfFormater'
import { phoneMask } from '@/app/utils/phoneFormater'
import { Button } from '@/view/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/view/components/ui/dialog'
import { Input } from '@/view/components/ui/input'
import { Label } from '@/view/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/view/components/ui/select'
import { Spinner } from '@/view/components/ui/spinner'
import { Textarea } from '@/view/components/ui/textarea'

import { useSupportRequestModal } from './useSupportRequestModal'

interface SupportRequestModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Antes do login: pede dados de contato e usa a rota pública. */
  unauthenticated?: boolean
  context?: SupportRequestModalContext
}

const formatFileSize = (size: number) => {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`

  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

export const SupportRequestModal = ({
  open,
  onOpenChange,
  unauthenticated = false,
  context,
}: SupportRequestModalProps) => {
  const {
    step,
    setStep,
    reason,
    setReason,
    description,
    setDescription,
    files,
    fileInputRef,
    handleSelectFiles,
    handleRemoveFile,
    handleOpenFilePicker,
    contact,
    updateContact,
    errors,
    errorMessage,
    reasons,
    isLoadingReasons,
    isPending,
    canSubmit,
    created,
    modalTitle,
    submitForm,
    openDirectWhatsAppSupport,
    openWhatsapp,
    maxAttachments,
  } = useSupportRequestModal({ open, onOpenChange, unauthenticated, context })

  const whatsappRow = (
    <button
      type="button"
      onClick={openDirectWhatsAppSupport}
      disabled={isPending}
      className="flex items-center justify-center gap-2 text-xs font-semibold text-black04 disabled:opacity-50"
    >
      {isPending ? (
        <Spinner className="h-4 w-4" />
      ) : (
        <img src="/whatsapp_green.svg" alt="" className="h-5 w-5" />
      )}
      Conversar agora com o suporte
    </button>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-md overflow-y-auto rounded-2xl"
        showSupportButton={false}
      >
        <DialogHeader>
          <DialogTitle className="pr-8 text-lg font-semibold">
            {modalTitle}
          </DialogTitle>
        </DialogHeader>

        {created ? (
          <div className="flex flex-col items-center gap-4 py-2">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Check className="h-8 w-8 text-primary" />
            </div>

            <p className="text-center font-semibold">
              Chamado {created.number} aberto
            </p>

            {!unauthenticated && (
              <p className="text-center text-sm text-black04">
                Acompanhe o andamento em &quot;Meus chamados&quot;. Você será
                avisado a cada atualização.
              </p>
            )}

            <Button
              className="w-full text-white"
              onClick={() => openWhatsapp(created.number)}
            >
              Abrir no WhatsApp
            </Button>
          </div>
        ) : step === 'choice' ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-black04">Como prefere receber ajuda?</p>

            <button
              type="button"
              onClick={openDirectWhatsAppSupport}
              disabled={isPending}
              className="flex items-center gap-3 border-b border-gray01 py-3 text-left disabled:opacity-50"
            >
              {isPending ? (
                <Spinner className="h-5 w-5" />
              ) : (
                <img
                  src="/whatsapp_green.svg"
                  alt=""
                  className="h-6 w-6 shrink-0"
                />
              )}
              <span className="flex flex-col">
                <span className="text-sm font-semibold">Falar no WhatsApp</span>
                <span className="text-xs text-black04">
                  Conversar agora com o suporte
                </span>
              </span>
            </button>

            <button
              type="button"
              onClick={() => setStep('form')}
              disabled={isPending}
              className="flex items-center gap-3 py-3 text-left disabled:opacity-50"
            >
              <Headset className="h-6 w-6 shrink-0" />
              <span className="flex flex-col">
                <span className="text-sm font-semibold">Abrir um chamado</span>
                <span className="text-xs text-black04">
                  Registrar solicitação e acompanhar o status
                </span>
              </span>
            </button>

            {!!errorMessage && (
              <p className="text-xs font-medium text-error">{errorMessage}</p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {unauthenticated && (
              <>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="support-name">Nome *</Label>
                  <Input
                    id="support-name"
                    placeholder="Insira seu nome"
                    value={contact.name ?? ''}
                    onChange={(event) =>
                      updateContact('name', event.target.value)
                    }
                  />
                  {errors.name && (
                    <p className="text-xs text-error">{errors.name}</p>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="support-cpf">CPF *</Label>
                  <Input
                    id="support-cpf"
                    placeholder="000.000.000-00"
                    inputMode="numeric"
                    value={contact.cpf ?? ''}
                    onInput={cpfMask}
                    onChange={(event) =>
                      updateContact('cpf', event.target.value)
                    }
                  />
                  {errors.cpf && (
                    <p className="text-xs text-error">{errors.cpf}</p>
                  )}
                </div>

                <p className="text-sm font-medium">
                  Como podemos entrar em contato?
                </p>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="support-phone">Telefone</Label>
                  <Input
                    id="support-phone"
                    placeholder="(00) 99999-9999"
                    inputMode="numeric"
                    value={contact.phone ?? ''}
                    onInput={phoneMask}
                    onChange={(event) =>
                      updateContact('phone', event.target.value)
                    }
                  />
                  {errors.phone && (
                    <p className="text-xs text-error">{errors.phone}</p>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="support-email">Email</Label>
                  <Input
                    id="support-email"
                    placeholder="exemplo@dominio.com"
                    value={contact.email ?? ''}
                    onChange={(event) =>
                      updateContact('email', event.target.value)
                    }
                  />
                  {errors.email && (
                    <p className="text-xs text-error">{errors.email}</p>
                  )}
                </div>
              </>
            )}

            <div className="flex flex-col gap-2">
              <Label htmlFor="support-reason">Motivo *</Label>
              {isLoadingReasons ? (
                <p className="text-sm text-black04">Carregando motivos…</p>
              ) : (
                <Select
                  value={reason ?? undefined}
                  onValueChange={(value) =>
                    setReason(value as SupportRequestReason)
                  }
                >
                  <SelectTrigger id="support-reason">
                    <SelectValue placeholder="Selecione o motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    {reasons.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="support-description">Descrição (opcional)</Label>
              <Textarea
                id="support-description"
                placeholder="Descreva o problema (opcional)"
                className="min-h-[90px] resize-none"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label>Anexos (opcional)</Label>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime"
                multiple
                className="hidden"
                onChange={handleSelectFiles}
              />

              {files.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="flex items-center gap-2 rounded-lg border border-gray-300 p-2"
                >
                  {file.type.startsWith('video/') ? (
                    <Video className="h-4 w-4 shrink-0" />
                  ) : (
                    <ImageIcon className="h-4 w-4 shrink-0" />
                  )}
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-xs text-gray-700">
                      {file.name}
                    </span>
                    <span className="text-[10px] text-black04">
                      {formatFileSize(file.size)}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    disabled={isPending}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}

              {files.length < maxAttachments && (
                <button
                  type="button"
                  onClick={handleOpenFilePicker}
                  className="flex w-fit items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-black03"
                >
                  <Paperclip className="h-4 w-4" />
                  Adicionar imagem/vídeo
                </button>
              )}

              <span className="text-[11px] text-black04">
                {files.length}/{maxAttachments} arquivos
              </span>
            </div>

            {!!errorMessage && (
              <p className="text-xs font-medium text-error">{errorMessage}</p>
            )}

            <Button
              className={cn('w-full text-white')}
              onClick={submitForm}
              disabled={!canSubmit}
            >
              {isPending ? <Spinner /> : 'Enviar solicitação'}
            </Button>

            {whatsappRow}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
